import type { GeoJSONSource, Map as MaplibreMap } from "maplibre-gl";
import { bbox, buffer } from "@turf/turf";
import { PLAElementRecord, GeoPoint, PLAElementTypeDefinition } from "./PLAState";

export const PLA_SOURCE_ID = "pla-elements";
export const PLA_POLYGON_LAYER_ID = "pla-elements-poly";
export const PLA_POLYGON_OUTLINE_LAYER_ID = "pla-elements-poly-outline";
export const PLA_POINT_LAYER_ID = "pla-elements-point";
export const PLA_HIGHLIGHT_SOURCE_ID = "pla-highlight";
export const PLA_HIGHLIGHT_LAYER_ID = "pla-highlight-layer";

export interface PLAMapLayerHandlers {
   onFeatureClick: (elementId: string) => void;
}

export function normalizePLAGeoPoint(point: GeoPoint | undefined): [number, number] | undefined {
   if (!point) return undefined;
   if (Array.isArray(point)) return [Number(point[0]), Number(point[1])];
   if ("lon" in point) return [Number(point.lon), Number(point.lat)];
   return [Number(point.longitude), Number(point.latitude)];
}

export function createPLAGeoJson(elements: PLAElementRecord[], alertsByElementId: Record<string, string[]> = {}, elementTypes: Record<string, PLAElementTypeDefinition> = {}) {
   return {
      type: "FeatureCollection" as const,
      features: elements.flatMap((element) => {
         const hasAlert = (alertsByElementId[element.id] || []).length > 0;
         const color = elementTypes[element.elementType]?.color || "#2d8f85";
         if (element.geoShape) {
            return [{
               id: element.id,
               type: "Feature" as const,
               geometry: element.geoShape.type === "Feature" ? element.geoShape.geometry : element.geoShape,
               properties: { id: element.id, elementType: element.elementType, name: element.name, hasAlert, color }
            }];
         }
         const coordinates = normalizePLAGeoPoint(element.geoPoint);
         if (!coordinates) return [];
         return [{
            id: element.id,
            type: "Feature" as const,
            geometry: { type: "Point" as const, coordinates },
            properties: { id: element.id, elementType: element.elementType, name: element.name, hasAlert, color }
         }];
      })
   };
}

export function setupPLAMapLayers(map: MaplibreMap, elements: PLAElementRecord[], alertsByElementId: Record<string, string[]>, handlers: PLAMapLayerHandlers, elementTypes: Record<string, PLAElementTypeDefinition> = {}): () => void {
   const geoJson = createPLAGeoJson(elements, alertsByElementId, elementTypes);
   if (map.getSource(PLA_SOURCE_ID)) {
      (map.getSource(PLA_SOURCE_ID) as GeoJSONSource).setData(geoJson as any);
   } else {
      map.addSource(PLA_SOURCE_ID, { type: "geojson", promoteId: "id", data: geoJson as any });
   }

   if (!map.getLayer(PLA_POLYGON_LAYER_ID)) {
      map.addLayer({
         id: PLA_POLYGON_LAYER_ID,
         type: "fill",
         source: PLA_SOURCE_ID,
         filter: ["==", ["geometry-type"], "Polygon"],
         paint: {
            "fill-color": ["case", ["to-boolean", ["get", "hasAlert"]], "#d88a15", ["get", "color"]],
            "fill-opacity": ["case", ["to-boolean", ["feature-state", "selected"]], 0.65, 0.38]
         }
      } as any);
   }
   if (!map.getLayer(PLA_POLYGON_OUTLINE_LAYER_ID)) {
      map.addLayer({
         id: PLA_POLYGON_OUTLINE_LAYER_ID,
         type: "line",
         source: PLA_SOURCE_ID,
         filter: ["==", ["geometry-type"], "Polygon"],
         paint: {
            "line-color": ["case", ["to-boolean", ["get", "hasAlert"]], "#ffcc33", ["get", "color"]],
            "line-width": ["case", ["to-boolean", ["feature-state", "selected"]], 4, 1.5]
         }
      } as any);
   }
   if (!map.getLayer(PLA_POINT_LAYER_ID)) {
      map.addLayer({
         id: PLA_POINT_LAYER_ID,
         type: "circle",
         source: PLA_SOURCE_ID,
         filter: ["==", ["geometry-type"], "Point"],
         paint: {
            "circle-color": ["case", ["to-boolean", ["get", "hasAlert"]], "#d88a15", ["get", "color"]],
            "circle-radius": ["case", ["to-boolean", ["feature-state", "selected"]], 13, 9],
            "circle-stroke-width": 2,
            "circle-stroke-color": "#ffffff"
         }
      } as any);
   }

   const clickHandler = (event: any) => {
      event.preventDefault?.();
      const elementId = event.features?.[0]?.properties?.id;
      if (elementId) handlers.onFeatureClick(elementId);
   };
   const mouseMoveHandler = () => { map.getCanvas().style.cursor = "pointer"; };
   const mouseLeaveHandler = () => { map.getCanvas().style.cursor = ""; };

   [PLA_POINT_LAYER_ID, PLA_POLYGON_LAYER_ID].forEach((layerId) => {
      map.on("click", layerId, clickHandler);
      map.on("mousemove", layerId, mouseMoveHandler);
      map.on("mouseleave", layerId, mouseLeaveHandler);
   });

   return () => removePLAMapLayers(map, clickHandler, mouseMoveHandler, mouseLeaveHandler);
}

export function removePLAMapLayers(map?: MaplibreMap, clickHandler?: any, mouseMoveHandler?: any, mouseLeaveHandler?: any) {
   if (!map) return;
   [PLA_POINT_LAYER_ID, PLA_POLYGON_LAYER_ID].forEach((layerId) => {
      if (map.getLayer(layerId) && clickHandler) map.off("click", layerId, clickHandler);
      if (map.getLayer(layerId) && mouseMoveHandler) map.off("mousemove", layerId, mouseMoveHandler);
      if (map.getLayer(layerId) && mouseLeaveHandler) map.off("mouseleave", layerId, mouseLeaveHandler);
   });
   [PLA_HIGHLIGHT_LAYER_ID, PLA_POINT_LAYER_ID, PLA_POLYGON_OUTLINE_LAYER_ID, PLA_POLYGON_LAYER_ID].forEach((layerId) => {
      if (map.getLayer(layerId)) map.removeLayer(layerId);
   });
   [PLA_HIGHLIGHT_SOURCE_ID, PLA_SOURCE_ID].forEach((sourceId) => {
      if (map.getSource(sourceId)) map.removeSource(sourceId);
   });
   map.getCanvas().style.cursor = "";
}

export function setPLASelectedFeature(map: MaplibreMap, selectedElementId?: string) {
   const source = map.getSource(PLA_SOURCE_ID);
   if (!source) return;
   const features = (source as any)._data?.features || [];
   features.forEach((feature: any) => {
      map.setFeatureState({ source: PLA_SOURCE_ID, id: feature.id }, { selected: feature.id === selectedElementId });
   });
}

export function buildPLAZoomFeature(element: PLAElementRecord): any | undefined {
   if (element.geoShape) {
      return { type: "Feature", geometry: element.geoShape.type === "Feature" ? element.geoShape.geometry : element.geoShape, properties: {} };
   }
   const coordinates = normalizePLAGeoPoint(element.geoPoint);
   if (!coordinates) return undefined;
   const pointFeature = { type: "Feature", geometry: { type: "Point", coordinates }, properties: {} };
   return buffer(pointFeature as any, 0.25, { units: "kilometers" });
}

export function getPLABounds(element: PLAElementRecord): [number, number, number, number] | undefined {
   const zoomFeature = buildPLAZoomFeature(element);
   return zoomFeature ? bbox(zoomFeature as any) as [number, number, number, number] : undefined;
}
